import { Button } from '@daily/shared/components/Button';
import { Text } from '@daily/shared/components/Text';
import {
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakouts } from '../../contexts/BreakoutsProvider';
import { useStyleVariants } from '../../hooks/useStyleVariants';
import { MAIN_ROOM_IDENTIFIER } from '../BreakoutRooms/helpers/constants';
import { useBreakoutSessionState } from '../BreakoutRooms/hooks/useBreakoutSessionState';
import { useRoomMapping } from '../BreakoutRooms/hooks/useRoomMapping';

interface Props {
  name: string;
}

export const BreakoutRoomHeader: React.FC<Props> = memo(({ name }) => {
  const { t } = useTranslation();
  const { textVariantStrong } = useStyleVariants();
  const {
    breakoutHost,
    breakoutMapping,
    breakoutParticipantIds,
    breakoutParticipantsByRoomName,
    currentBreakoutRoom,
    mainRoomParticipantIds,
    participantsAskingForHelp,
  } = useBreakoutSessionState();
  const { moveParticipants } = useBreakouts();
  const { updateRoomMapping } = useRoomMapping();
  const isMainRoom = name === MAIN_ROOM_IDENTIFIER;

  const localSessionId = useLocalSessionId();
  const isOwner = useParticipantProperty(localSessionId, 'owner');

  const count = useMemo(() => {
    if (isMainRoom)
      return mainRoomParticipantIds.length === 0
        ? t('breakouts.noPeopleInRoom')
        : t('breakouts.peopleInRoom', {
            count: mainRoomParticipantIds.length,
          });
    return isOwner
      ? breakoutParticipantsByRoomName(name)[0]?.length === 0
        ? t('breakouts.noPeopleInRoom')
        : t('breakouts.peopleInRoom', {
            count: breakoutParticipantsByRoomName(name)[0]?.length,
          })
      : !breakoutParticipantIds.length
      ? t('breakouts.noPeopleInRoom')
      : t('breakouts.peopleInRoom', {
          count: breakoutParticipantIds.length,
        });
  }, [
    breakoutParticipantIds.length,
    breakoutParticipantsByRoomName,
    isMainRoom,
    isOwner,
    mainRoomParticipantIds.length,
    name,
    t,
  ]);

  const handleClickJoin = () => {
    let id: string;
    if (isMainRoom) {
      id = MAIN_ROOM_IDENTIFIER;
    } else {
      id = Object.entries(breakoutMapping).find(
        ([, room]) => room.name === name
      )[0];
    }
    updateRoomMapping(localSessionId, id);
    moveParticipants({
      [localSessionId]: isMainRoom ? null : id,
    });
  };

  const hasJoined =
    (!currentBreakoutRoom.name && isMainRoom) ||
    currentBreakoutRoom.name === name;

  const hasParticipantsAskingForHelp = isMainRoom
    ? participantsAskingForHelp.some((id) =>
        mainRoomParticipantIds.includes(id)
      )
    : Object.values(breakoutMapping)
        .find((room) => room.name === name)
        .users.some((id) => participantsAskingForHelp.includes(id));

  return (
    <div className="room-header">
      {breakoutHost === localSessionId && !hasJoined && (
        <Button
          className="join"
          onClick={handleClickJoin}
          size="small"
          variant={hasParticipantsAskingForHelp ? 'primary' : 'secondary'}
        >
          {t('breakouts.joinRoom')}
        </Button>
      )}
      <Text className="room-name" variant={textVariantStrong}>
        {isMainRoom
          ? t('breakouts.mainRoom')
          : t('breakouts.roomWithName', {
              name,
            })}
      </Text>
      <Text variant="small">{count}</Text>
      <style jsx>{`
        .room-header {
          padding-top: calc(13px + var(--gap));
        }
        .room-header :global(.join) {
          float: right;
        }
        .room-header :global(.room-name) {
          display: block;
        }
      `}</style>
    </div>
  );
});
BreakoutRoomHeader.displayName = 'BreakoutRoomHeader';
